/**
 * InformationSharingRollup (#328 Phase 2, ruling R2) — the ONE Coordination roll-up pane named
 * "Information sharing" (§2.2). The Liaison's single pane for a scoped incident: inbound + outbound
 * + every out-of-app leg in ONE list (interops messages, inbound assistance requests, outbound
 * agency tasks). Direction is DERIVED (A.2), never stored. R3: interops rows show the RECORDED
 * onward legs only — never a fabricated federal/ministry acknowledgement.
 *
 * Coordination is not itself incident-scoped, so this pane carries a lightweight incident picker
 * (the roll-up is per-incident).
 */
import { useEffect, useState, type JSX } from 'react';
import type { Incident, InteropsRollupItem } from '@tsr/shared-types';
import { fetchIncidents, fetchInteropsRollup } from './api';
import { RecipientBreakdown } from './RecipientBreakdown';
import { INFORMATION_SHARING_LABEL, INFORMATION_SHARING_SUBTEXT } from './interopsVocabulary';

/** Compact `HH:MM` (UTC) from an ISO stamp. */
function hhmm(iso: string): string {
  return iso.length >= 16 ? `${iso.slice(5, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

export function InformationSharingRollup({
  defaultIncidentId,
  onActiveCountChange,
}: {
  defaultIncidentId?: string;
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [incidentId, setIncidentId] = useState<string | null>(defaultIncidentId ?? null);
  const [items, setItems] = useState<InteropsRollupItem[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    void fetchIncidents({ status: 'open' }, ac.signal)
      .then((list) => {
        setIncidents(list);
        setIncidentId((cur) => cur ?? list[0]?.id ?? null);
      })
      .catch(() => {});
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!incidentId) {
      setItems([]);
      onActiveCountChange?.(0);
      return;
    }
    const ac = new AbortController();
    void fetchInteropsRollup(incidentId, ac.signal)
      .then((r) => {
        setItems(r.items);
        onActiveCountChange?.(r.items.length);
      })
      .catch(() => {});
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  return (
    <section className="flex h-full flex-col" data-testid="information-sharing-rollup">
      <div className="border-b border-slate-800 bg-slate-900/40 px-3 pt-2.5 pb-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-200">
          {INFORMATION_SHARING_LABEL}
        </h3>
        <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{INFORMATION_SHARING_SUBTEXT}</p>
        <select
          value={incidentId ?? ''}
          onChange={(e) => setIncidentId(e.target.value || null)}
          data-testid="rollup-incident-select"
          className="mt-1.5 w-full rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[11px] text-slate-100"
        >
          {incidents.length === 0 && <option value="">No incidents</option>}
          {incidents.map((i) => (
            <option key={i.id} value={i.id}>
              {i.incident_number} — {i.name}
            </option>
          ))}
        </select>
      </div>

      <ul className="min-h-0 flex-1 divide-y divide-slate-800 overflow-y-auto" data-testid="rollup-list">
        {items.length === 0 ? (
          <li className="px-3 py-4 text-[11px] text-slate-500">No information shared for this incident yet.</li>
        ) : (
          items.map((it) => (
            <li key={`${it.source}:${it.id}`} data-testid="rollup-item" data-direction={it.direction} className="px-3 py-2">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${
                    it.direction === 'inbound'
                      ? 'bg-violet-600/30 text-violet-200'
                      : 'bg-amber-600/30 text-amber-200'
                  }`}
                >
                  {it.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                </span>
                <span className="text-[11px] font-medium text-slate-200">{it.party_label}</span>
                <span className="ml-auto rounded bg-slate-700/50 px-1.5 py-px text-[9px] uppercase tracking-wide text-slate-300">
                  {it.kind_label}
                </span>
              </div>
              <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-[12px] text-slate-100">
                {it.summary}
              </p>
              <div className="mt-0.5 flex items-center gap-2 text-[9px] text-slate-500">
                <span className="font-mono">{hhmm(it.at)}</span>
                {it.status && <span className="uppercase tracking-wide">{it.status}</span>}
              </div>
              {it.recipients && it.recipients.length > 0 && (
                <div className="mt-1.5">
                  <RecipientBreakdown recipients={it.recipients} sentAt={it.at} />
                </div>
              )}
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
